import {
  ApiBlueprint,
  createApiRef,
  createApiFactory,
  discoveryApiRef,
  fetchApiRef,
  ProfileInfoApi,
  BackstageIdentityApi,
  SessionApi,
  OAuthApi,
  OpenIdConnectApi,
} from '@backstage/frontend-plugin-api';
import { assignLocation, replaceLocation } from '../navigation';

// Create admin auth API reference
export const adminAuthApiRef = createApiRef<
  OAuthApi & OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
>({
  id: 'auth.admin',
});

// Refresh token at most this far in advance of its expiry (seconds).
const TOKEN_REFRESH_BUFFER_SECONDS = 60;

type AdminIdentity = {
  type: 'user';
  userEntityRef: string;
  ownershipEntityRefs: string[];
  token: string;
  identity: {
    type: 'user';
    userEntityRef: string;
    ownershipEntityRefs: string[];
  };
};

// Admin auth API implementation using proper Backstage APIs
export class AdminAuthApi implements OAuthApi, OpenIdConnectApi, ProfileInfoApi, BackstageIdentityApi, SessionApi {
  private sessionState = 'SignedOut';
  private sessionObservers: Array<(state: string) => void> = [];
  private discoveryApi: any;
  private cachedIdentity?: AdminIdentity;
  private cachedExpiresAt = 0;
  private inFlightRefresh?: Promise<AdminIdentity | undefined>;

  constructor(discoveryApi: any, _fetchApi: any) {
    this.discoveryApi = discoveryApi;
    // Polling only makes sense in dev/test where the admin login button exists;
    // in production AdminAuthApi is registered but never used, so polling would
    // hammer /api/auth/admin/refresh every 5s for no reason.
    if (process.env.NODE_ENV !== 'production') {
      this.checkAuthState();
    }
  }

  private async checkAuthState() {
    try {
      const baseUrl = await this.discoveryApi.getBaseUrl('auth');
      const response = await fetch(`${baseUrl}/admin/refresh`, {
        credentials: 'include',
      });

      const newState = response.ok ? 'SignedIn' : 'SignedOut';

      if (newState !== this.sessionState) {
        this.sessionState = newState;
        this.notifyObservers();
      }
    } catch (error) {
      if (this.sessionState !== 'SignedOut') {
        this.sessionState = 'SignedOut';
        this.notifyObservers();
      }
    }

    setTimeout(() => this.checkAuthState(), 5000);
  }

  private notifyObservers() {
    this.sessionObservers.forEach(observer => observer(this.sessionState));
  }

  private buildIdentity(token: string): AdminIdentity {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const userEntityRef = payload.sub || 'user:default/test-admin';
    const ownershipEntityRefs = payload.ent || ['group:default/dev-platform-admin'];
    return {
      type: 'user',
      userEntityRef,
      ownershipEntityRefs,
      token,
      identity: { type: 'user', userEntityRef, ownershipEntityRefs },
    };
  }

  private isCacheFresh(): boolean {
    if (!this.cachedIdentity) {
      return false;
    }
    const nowSeconds = Math.floor(Date.now() / 1000);
    return this.cachedExpiresAt - TOKEN_REFRESH_BUFFER_SECONDS > nowSeconds;
  }

  private async refreshIdentity(): Promise<AdminIdentity | undefined> {
    if (this.inFlightRefresh) {
      return this.inFlightRefresh;
    }
    this.inFlightRefresh = (async () => {
      try {
        const baseUrl = await this.discoveryApi.getBaseUrl('auth');
        const response = await fetch(`${baseUrl}/admin/refresh?optional=false`, {
          credentials: 'include',
        });
        if (!response.ok) {
          throw new Error(`Authentication failed: ${response.status}`);
        }
        const data = await response.json();
        const token: string | undefined = data.backstageIdentity?.token;
        if (!token) {
          throw new Error('No authentication token received');
        }
        const identity = this.buildIdentity(token);
        const payload = JSON.parse(atob(token.split('.')[1]));
        this.cachedIdentity = identity;
        this.cachedExpiresAt = typeof payload.exp === 'number' ? payload.exp : 0;
        if (this.sessionState !== 'SignedIn') {
          this.sessionState = 'SignedIn';
          this.notifyObservers();
        }
        return identity;
      } finally {
        this.inFlightRefresh = undefined;
      }
    })();
    return this.inFlightRefresh;
  }

  async getProfile() {
    return {
      email: 'admin@example.com',
      displayName: 'Admin User',
      picture: undefined,
    };
  }

  async getBackstageIdentity(options?: any) {
    if (options?.instantPopup) {
      try {
        return await this.refreshIdentity();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Admin authentication error:', error);
        return undefined;
      }
    }

    if (this.isCacheFresh()) {
      return this.cachedIdentity;
    }

    // On a fresh page load the in-memory cache is empty. The new-frontend
    // SignInPage restores a session by calling getBackstageIdentity({ optional:
    // true }); if we returned undefined here without trying the refresh
    // endpoint, the (valid, cookie-backed) admin session would not be restored
    // and the sign-in chooser would be shown on every reload / deep link.
    // Attempt a silent refresh and only fall back to undefined when it fails.
    if (options?.optional) {
      try {
        return await this.refreshIdentity();
      } catch {
        return undefined;
      }
    }

    return this.refreshIdentity();
  }

  async getCredentials() {
    try {
      if (this.isCacheFresh() && this.cachedIdentity) {
        return { token: this.cachedIdentity.token };
      }
      const identity = await this.refreshIdentity();
      return identity ? { token: identity.token } : {};
    } catch (error) {
      return {};
    }
  }

  async signIn() {
    try {
      const baseUrl = await this.discoveryApi.getBaseUrl('auth');
      const response = await fetch(`${baseUrl}/admin/refresh`, {
        credentials: 'include',
      });

      if (response.ok) {
        this.sessionState = 'SignedIn';
        this.notifyObservers();
        replaceLocation('/');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Admin authentication error:', error);
    }
  }

  async signOut() {
    this.sessionState = 'SignedOut';
    this.notifyObservers();
    assignLocation('/auth/admin/logout');
  }

  get sessionState$() {
    return {
      subscribe: (observer: (state: string) => void) => {
        this.sessionObservers.push(observer);
        observer('SignedOut');

        return {
          unsubscribe: () => {
            const index = this.sessionObservers.indexOf(observer);
            if (index > -1) {
              this.sessionObservers.splice(index, 1);
            }
          }
        };
      }
    } as any;
  }

  // OAuthApi methods - redirect to our custom signIn
  async getAccessToken(_scopes?: string | string[]): Promise<string> {
    await this.signIn();
    return '';
  }

  // OpenIdConnectApi methods - redirect to our custom signIn
  async getIdToken(_options?: any): Promise<string> {
    await this.signIn();
    return '';
  }

}

export const adminAuthApiExtension = ApiBlueprint.make({
  name: 'admin-auth',
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: adminAuthApiRef,
        deps: {
          discoveryApi: discoveryApiRef,
          fetchApi: fetchApiRef,
        },
        factory: ({ discoveryApi, fetchApi }) => {
          return new AdminAuthApi(discoveryApi, fetchApi);
        },
      }),
    ),
});
